/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Award, Target, Shield, TrendingUp, ChevronRight, CheckCircle, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import teamImage from "../Assets/team.jpeg";

const values = [
  { icon: Target, title: "Innovation", description: "Embrace creativity and forward-thinking solutions to push the boundaries of design and functionality." },
  { icon: Shield, title: "Quality", description: "Unwavering commitment to excellence in every aspect of our work." },
  { icon: Award, title: "Excellence", description: "Commit to high standards in design, craftsmanship, and client service to deliver superior results." },
  { icon: TrendingUp, title: "Sustainability", description: "Prioritize eco-friendly practices and materials to ensure that our projects contribute positively to the environment." },
  { icon: Users, title: "Collaboration", description: "Foster a team-oriented culture that values input from all stakeholders to achieve the best possible outcomes." },
  { icon: CheckCircle, title: "Integrity", description: "Maintain transparency, honesty and ethical standards in all business practices and client interactions." },
];

const milestones = [
  { year: "2018", event: "Company Founded", description: "BuildFast Structural Solutions was established in Harare." },
  { year: "2020", event: "First Major Project", description: "Completed our first commercial building project in the CBD." },
  { year: "2022", event: "Expansion", description: "Opened additional office and expanded services to include engineering." },
  { year: "2023", event: "Industry Recognition", description: "Received multiple awards for architectural excellence and innovation." },
];

export const About = ({title}) => {
  useEffect(() => { document.title = title; }, [title]);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative py-24 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/20 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-20 h-1 bg-yellow-400 rounded-full mx-auto mb-6"></div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              About <span className="text-yellow-400">BuildFast</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto mb-10">
              Excellence in Architecture & Engineering Since 2018
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8">
              {[
                { number: "10+", label: "Years Experience" },
                { number: "200+", label: "Projects Completed" },
                { number: "50+", label: "Team Members" },
                { number: "100%", label: "Client Satisfaction" }
              ].map((stat, i) => (
                <motion.div key={i} initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 + i * 0.1 }} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">{stat.number}</div>
                  <div className="text-sm md:text-base text-gray-300">{stat.label}</div>
                </motion.div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link to="/contact-us" className="px-8 py-4 bg-yellow-400 text-blue-800 font-semibold rounded-xl hover:bg-yellow-300 transition-colors duration-300 flex items-center justify-center gap-2 group">
                Get Started <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/projects" className="px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors duration-300 flex items-center justify-center gap-2 group">
                View Projects <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-800 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ x: -50, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-12 bg-yellow-400"></div>
                <span className="text-blue-800 font-semibold">Our Story</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Building Dreams Into Reality</h2>
              <p className="text-gray-600 mb-6 text-lg">With over 5 years of experience in architectural excellence, we've been transforming spaces and creating innovative solutions that inspire. Our journey began with a vision to redefine construction in Zimbabwe.</p>
              <div className="space-y-4">
                {["Innovative design solutions", "Comprehensive project management", "Sustainable building practices", "Award-winning architectural team"].map((item, index) => (
                  <motion.div key={index} initial={{ x: -20, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} transition={{ delay: index * 0.1 }} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ x: 50, opacity: 0 }} whileInView={{ x: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
              <img src={teamImage} alt="BuildFast Team" className="rounded-2xl shadow-2xl w-full h-auto object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-1 w-12 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold">Our Values</span>
              <div className="h-1 w-12 bg-yellow-400"></div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Drives Us Forward</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div key={index} initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: index * 0.1 }} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-yellow-400/50">
                <div className="bg-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-8 h-8 text-blue-800" />
                </div>
                <h3 className="text-xl font-bold text-blue-800 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-1 w-12 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold">Our Journey</span>
              <div className="h-1 w-12 bg-yellow-400"></div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Milestones</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <motion.div key={index} initial={{ y: 50, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: index * 0.1 }} className="relative bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="absolute -top-4 left-6 bg-yellow-400 text-blue-900 px-4 py-2 rounded-full font-bold text-sm">{milestone.year}</div>
                <h3 className="text-xl font-bold text-blue-800 mt-4 mb-3">{milestone.event}</h3>
                <p className="text-gray-600">{milestone.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-800 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Build Your Dream?</h2>
          <p className="text-blue-200 mb-8 text-lg">Let's discuss how we can bring your vision to life with our expertise and dedication.</p>
          <Link to="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-400 text-blue-900 font-bold rounded-xl hover:bg-yellow-300 transition-colors">
            Contact Us Today <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </main>
  );
};
